import { useQuery } from '@apollo/client';
import { useContext, useEffect, useState } from 'react';

import { NetworkStatusValuesContext, BookValuesContext } from 'contexts';
import { useLazyQueryPaginated } from 'hooks';
import { getIDBObjects } from 'utilities/idbUtilities';

/**
 * This hook wraps `useQuery` or `useLazyQueryPaginated` and stores successful responses in IndexedDB,
 * returning them instead of an error when the app is offline.
 *
 * NOTE:
 *  If this hook has paginated = true, `...onlineProperties` will return the following additional props
 *  that will need be used to execute the query request manually. See `useQueryForUserContent.js` for an example.
 *  { called, isFinished, loading, getData, fetchMoreData, refetch }
 */
export default function useQueryOffline(query, options) {
  const {
    skip,
    variables,
    paginated = false,
    // if we use merged fields, we update idb when they get merged together
    // in useQueryForUserContent
    shouldUseMergedFields = false,
    ...otherOptions
  } = options;

  /**
   * When `useQueryOffline` is called in BookProvider, it does not have access to BookValuesContext.
   * For that case, we can pull the book pk from the variables object.
   */
  const bookPk = useContext(BookValuesContext)?.book?.pk ?? variables?.epubId;
  const { isOffline } = useContext(NetworkStatusValuesContext);
  const queryName = query.definitions[0].name.value;

  const [key, setKey] = useState();
  const [offlineData, setOfflineData] = useState();

  // When offline, skip the query so no GraphQL request is sent; data comes from offlineData (IndexedDB).
  const {
    data: onlineData,
    error: onlineError,
    ...onlineProperties
  } = (paginated ? useLazyQueryPaginated : useQuery)(query, {
    skip: skip || isOffline,
    variables,
    ...otherOptions,
  });

  // Set the IndexedDB key for the graphql data.
  useEffect(() => {
    /**
     * The `variables` object will include bookPk, and the IndexedDB database will already be keyed by bookPk.
     * It is redundant, but there is no harm in including it to avoid the need to strip it out.
     */
    if (shouldUseMergedFields) {
      return;
    }
    setKey(`${queryName} ${JSON.stringify(variables)}`);
  }, [queryName, variables, shouldUseMergedFields]);

  // Pull existing data from IndexedDB if it is available.
  useEffect(() => {
    if (!bookPk || !key) {
      return;
    }

    if (shouldUseMergedFields) {
      return;
    }

    getIDBObjects(bookPk, 'graphql').then((idbObjects) => {
      if (idbObjects) {
        const { store } = idbObjects;
        store.get(key).then((data) => setOfflineData(data));
      }
    });
  }, [bookPk, key, shouldUseMergedFields]);

  /**
   * Update the value of offlineData to the the latest successful `useQuery` result whenever it runs.
   * It will be used if the app reloads. The current session will reference the `offlineData` variable.
   */
  useEffect(() => {
    if (!bookPk || isOffline || !onlineData || onlineError) {
      return;
    }

    if (JSON.stringify(offlineData) === JSON.stringify(onlineData)) {
      // Do nothing if the data did not change.
      return;
    }

    if (shouldUseMergedFields) {
      setOfflineData(onlineData);
      return;
    }

    // when online and request succeeding
    // store the current successful graphql result
    getIDBObjects(bookPk, 'graphql', true).then((idbObjects) => {
      if (idbObjects) {
        const { store } = idbObjects;
        store.put(onlineData, key);
      }
    });

    setOfflineData(onlineData);
  }, [
    bookPk,
    isOffline,
    key,
    offlineData,
    onlineData,
    onlineError,
    shouldUseMergedFields,
  ]);

  return {
    data: offlineData,
    error: isOffline ? null : onlineError, // pass through `useQuery` errors when online
    ...onlineProperties,
  };
}
